import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Avatar, Snackbar } from "@mui/material";
import "./userProfilePage.css";
import PaymentActivationModal from "../matrimonyList/paymentActivationModal.tsx";
import { getSingleUser, getVerificationDetails, updateUserProfile, uploadVerificationDocuments } from "../../../services/authAPI.js";
import { updateUser } from "../../../redux/actions/authActions";
import EditProfileDialog from "./EditProfileDialog.tsx";
import VerificationUploadDialog from "./verificationUploadDialog.tsx";

type VerificationResponse = {
  verification?: {
    id_proof_front_url?: string | null;
    id_proof_back_url?: string | null;
    payment_receipt_url?: string | null;
  };
};

type SnackState = {
  open: boolean;
  message: string;
  severity: "success" | "error";
};

const UserProfilePage = () => {
  const authUser = useSelector((state: any) => state.auth.user);
  const dispatch = useDispatch();
  const [user, setUser] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileLoadError, setProfileLoadError] = useState("");
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const getInitial = (name?: string) => (name ? name.charAt(0).toUpperCase() : "?");
  const formatDate = (dob: string) => (dob ? new Date(dob).toLocaleDateString("en-GB") : "-");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [verification, setVerification] = useState<VerificationResponse | null>(null);
  const [uploadFiles, setUploadFiles] = useState({
    idProofFront: null,
    idProofBack: null,
    paymentReceipt: null,
  });
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const defaultEditData = {
    fullName: "",
    mobile: "",
    dob: "",
    birth_time: "",
    gender: "",
    mother_tongue: "",
    profile_created_by: "",
    marital_status: "",
    children: "",
    kids_live_with: "",
    religion: "",
    caste: "",
    zodiac: "",
    star: "",
    star_patham: "",
    body_type: "",
    physical_status: "",
    height: "",
    weight: "",
    complexion: "",

    birth_country: "",
    resident_country: "",
    resident_status: "",
    city: "",
    address: "",

    education: "",
    occupation: "",

    father_place: "",
    mother_place: "",
    father_caste: "",
    mother_caste: "",
    father_occupation: "",
    mother_occupation: "",

    hobbies: "",
    eating_habit: "",
    drinking_habit: "",
    smoking_habit: "",

    kiraga_paavam: "",
    sevvai_lakna: "",

    about: "",
    partner_qualities: "",
    photo: null,
    horoscope: null,
  };

  const [editData, setEditData] = useState(defaultEditData);
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: "",
    severity: "success",
  });

  // Use Redux only to identify the signed-in user; render details from the GET response.
  useEffect(() => {
    if (!authUser?.id) {
      setProfileLoading(false);
      setProfileLoadError("Could not determine the signed-in user's ID.");
      return;
    }

    let cancelled = false;
    const loadUserProfile = async () => {
      setProfileLoading(true);
      setProfileLoadError("");
      try {
        const result = await getSingleUser(authUser.id);
        if (!result?.user) throw new Error("The server returned no user details.");
        if (cancelled) return;

        setUser(result.user);
        dispatch(updateUser(result.user));
      } catch (error: any) {
        if (!cancelled) {
          setProfileLoadError(error.message || "Failed to load your profile details.");
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    };

    loadUserProfile();
    return () => {
      cancelled = true;
    };
  }, [authUser?.id, dispatch]);

  useEffect(() => {
    const loadVerification = async () => {
      try {
        const result = await getVerificationDetails(authUser.id);

        setVerification(result);
      } catch (error) {
        console.error(error);
      }
    };

    if (authUser?.id) {
      loadVerification();
    }
  }, [authUser?.id]);

  const handleUploadDocuments = async () => {
    try {
      setUploading(true);

      const updated = await uploadVerificationDocuments(user.id, uploadFiles);

      setVerification(updated.verification ? updated : { verification: updated });

      setOpenUploadModal(false);

      setSnack({
        open: true,
        severity: "success",
        message: "Documents uploaded successfully",
      });
    } catch (error: any) {
      console.error(error);

      setSnack({
        open: true,
        severity: "error",
        message: error.message || "Failed to upload documents",
      });
    } finally {
      setUploading(false);
    }
  };

  /* Helpers */
  const Item = ({ label, value }: any) => (
    <div className='profile-item'>
      <label>{label}</label>
      <span>{value || "-"}</span>
    </div>
  );

  useEffect(() => {
    if (user) {
      setEditData({
        fullName: user.name || "",
        mobile: user.mobile || "",
        dob: user.dob ? new Date(user.dob).toISOString().split("T")[0] : "",
        birth_time: user.birth_time || "",
        gender: user.gender || "",

        mother_tongue: user.mother_tongue || "",
        profile_created_by: user.profile_created_by || "",
        marital_status: user.marital_status || "",
        children: user.children || "",
        kids_live_with: user.kids_live_with || "",
        religion: user.religion || "",
        caste: user.caste || "",
        zodiac: user.zodiac || "",
        star: user.star || "",
        star_patham: user.star_patham || "",

        body_type: user.body_type || "",
        physical_status: user.physical_status || "",
        height: user.height || "",
        weight: user.weight || "",
        complexion: user.complexion || "",

        birth_country: user.birth_country || "",
        resident_country: user.resident_country || "",
        resident_status: user.resident_status || "",
        city: user.city || "",
        address: user.address || "",

        education: user.education || "",
        occupation: user.occupation || "",

        father_place: user.father_place || "",
        mother_place: user.mother_place || "",
        father_caste: user.father_caste || "",
        mother_caste: user.mother_caste || "",
        father_occupation: user.father_occupation || "",
        mother_occupation: user.mother_occupation || "",

        hobbies: user.hobbies || "",
        eating_habit: user.eating_habit || "",
        drinking_habit: user.drinking_habit || "",
        smoking_habit: user.smoking_habit || "",

        kiraga_paavam: user.kiraga_paavam || "",
        sevvai_lakna: user.sevvai_lakna || "",

        about: user.about || "",
        partner_qualities: user.partner_qualities || "",
        photo: user.photo_url || null,
        horoscope: user.horoscope_url || null,
      });
    }
  }, [user, openEditModal]);

  const handleUpdateProfile = async () => {
    try {
      setSavingProfile(true);
      await updateUserProfile(user.id, editData);

      // Reload from the user-by-id endpoint and render only that response.
      setUser(null);
      setProfileLoading(true);
      const refreshed = await getSingleUser(user.id);
      if (!refreshed?.user) throw new Error("The server returned no user details after saving.");
      setUser(refreshed.user);
      dispatch(updateUser(refreshed.user));

      setSnack({
        open: true,
        message: "Profile updated successfully",
        severity: "success",
      });

      setOpenEditModal(false);
    } catch (err) {
      console.error(err);
      setUser(null);
      setProfileLoadError((err as any)?.message || "Failed to save or reload your profile.");
    } finally {
      setProfileLoading(false);
      setSavingProfile(false);
    }
  };

  const Section = ({ title, children }: any) => (
    <div className='profile-section'>
      <h3>{title}</h3>
      <div className='profile-section-grid'>{children}</div>
    </div>
  );

  if (profileLoading) return <div className='profile-loading'>Loading profile...</div>;
  if (profileLoadError || !user) {
    return <div className='profile-loading'>{profileLoadError || "Profile details are unavailable."}</div>;
  }

  const rawProfileStatus = user.profile_status || user.profileStatus;
  const profileStatus = typeof rawProfileStatus === "string" && rawProfileStatus.trim()
    ? rawProfileStatus.trim().toLowerCase()
    : "inactive";

  return (
    <>
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant='filled'>
          {snack.message}
        </Alert>
      </Snackbar>
      <div className='profile-page'>
        <div className='profile-container'>
          {/* HEADER CARD */}
          <div className='profile-hero-card'>
            <Avatar className='profile-avatar' src={user.photo_url || undefined}>
              {!user.photo_url && getInitial(user.name || user.email)}
            </Avatar>
            <div className='profile-hero-info'>
              <div className='profile-header'>
                <div>
                  <h2>{user.name}</h2>
                  <h4>Profile Id-: {user.id}</h4>
                </div>

                <div className='profile-header-actions'>
                  <button type='button' className='edit-profile-btn' onClick={() => setOpenEditModal(true)}>
                    Edit Profile
                  </button>
                </div>
              </div>
              {profileStatus === "inactive" ? (
                <div className='profile-tags'>
                  <span className={`account-status ${profileStatus}`}>{profileStatus.toUpperCase()}</span>

                  <div className='profile-message'>
                    <h5>
                      Upload your ID proof and payment receipt inside the verification documents section to complete
                      verification and unlock full access.
                    </h5>
                    <br />
                    <button type='button' className='view-btn' onClick={() => setOpenPaymentModal(true)}>
                      View Payment Details
                    </button>
                  </div>
                </div>
              ) : (
                <div className='profile-tags'>
                  <span className={`account-status ${profileStatus}`}>{profileStatus.toUpperCase()}</span>
                  <h5>
                    Now you are a verified member of Uyirththunai. You can now enjoy full access to all features and
                    services.
                  </h5>
                </div>
              )}
            </div>
          </div>

          {/* GRID SECTIONS */}
          <div className='profile-grid'>
            <Section title='Basic Info'>
              <Item label='Email' value={user.email} />
              <Item label='Phone' value={user.mobile} />
              <Item label='DOB' value={formatDate(user.dob)} />
              <Item label='Birth Time' value={user.birth_time} />
              <Item label='Gender' value={user.gender} />
              <Item label='Marital Status' value={user.marital_status} />
              <Item label='Religion' value={user.religion} />
              <Item label='Mother Tongue' value={user.mother_tongue} />
              <Item label='Profile Created By' value={user.profile_created_by} />
            </Section>

            <Section title='Religion & Astrology'>
              <Item label='Religion' value={user.religion} />
              <Item label='Caste' value={user.caste} />
              <Item label='Zodiac' value={user.zodiac} />
              <Item label='Star' value={user.star} />
              <Item label='Kiraga Paavam' value={user.kiraga_paavam} />
            </Section>

            <Section title='Physical Info'>
              <Item label='Body Type' value={user.body_type} />
              <Item label='Height' value={user.height} />
              <Item label='Weight' value={user.weight} />
              <Item label='Complexion' value={user.complexion} />
            </Section>

            <Section title='Location'>
              <Item label='Birth Country' value={user.birth_country} />
              <Item label='Resident Country' value={user.resident_country} />
              <Item label='City' value={user.city} />
            </Section>

            <Section title='Career'>
              <Item label='Education' value={user.education} />
              <Item label='Occupation' value={user.occupation} />
            </Section>

            <Section title='Family'>
              <Item label='Father Occupation' value={user.father_occupation} />
              <Item label='Mother Occupation' value={user.mother_occupation} />
            </Section>

            <Section title='Lifestyle'>
              <Item label='Hobbies' value={user.hobbies} />
              <Item label='Eating' value={user.eating_habit} />
              <Item label='Drinking' value={user.drinking_habit} />
              <Item label='Smoking' value={user.smoking_habit} />
            </Section>
          </div>

          {/* ABOUT */}
          <div className='profile-card'>
            <h3>About Me</h3>
            <p>{user.about || "No description added yet."}</p>
          </div>

          {/* PARTNER */}
          <div className='profile-card'>
            <h3>Partner Preferences</h3>
            <p>{user.partner_qualities || "No preference added."}</p>
          </div>

          {/* HOROSCOPE */}
          <div className='profile-card'>
            <h3>Horoscope</h3>

            {user.horoscope_url ? (
              <img src={user.horoscope_url} alt='Horoscope' className='horoscope-img' />
            ) : (
              <p className='empty-text'>No horoscope uploaded</p>
            )}
          </div>
          <div className='profile-card'>
            {/* HOROSCOPE */}
            <h3>Photo</h3>
            {user.photo_url ? (
              <img src={user.photo_url} alt='User profile' className='horoscope-img' />
            ) : (
              <p className='empty-text'>No photo uploaded</p>
            )}
          </div>
          <div className='profile-card documents-card'>
            <div className='documents-header'>
              <h3>Verification Documents</h3>

              <button className='upload-docs-btn' onClick={() => setOpenUploadModal(true)}>
                Upload Documents
              </button>
            </div>

            <div className='documents-grid'>
              <div className='document-card'>
                <h4>ID Proof Front</h4>

                {verification?.verification?.id_proof_front_url ? (
                  <img src={verification.verification.id_proof_front_url} alt='ID Front' className='document-img' />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>

              <div className='document-card'>
                <h4>ID Proof Back</h4>

                {verification?.verification?.id_proof_back_url ? (
                  <img src={verification.verification.id_proof_back_url} alt='ID Back' className='document-img' />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>

              <div className='document-card'>
                <h4>Payment Receipt</h4>

                {verification?.verification?.payment_receipt_url ? (
                  <img
                    src={verification.verification.payment_receipt_url}
                    alt='Payment Receipt'
                    className='document-img'
                  />
                ) : (
                  <div className='document-placeholder'>Not Uploaded</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaymentActivationModal open={openPaymentModal} onClose={() => setOpenPaymentModal(false)} />
      <EditProfileDialog
        open={openEditModal}
        onClose={() => setOpenEditModal(false)}
        editData={editData}
        setEditData={setEditData}
        onSave={handleUpdateProfile}
        saving={savingProfile}
      />
      <VerificationUploadDialog
        open={openUploadModal}
        onClose={() => setOpenUploadModal(false)}
        uploadFiles={uploadFiles}
        setUploadFiles={setUploadFiles}
        onUpload={handleUploadDocuments}
        uploading={uploading}
      />
    </>
  );
};

export default UserProfilePage;
